/**
 * The outbox: each write the person made, kept until the server has it (unit
 * 25B, `_docs/v2/areas/offline.md` rules 7 to 10).
 *
 * A queued write is only a request — a path, a method, a body — because each
 * one replays to its own endpoint (Q3), and every such endpoint is
 * state-setting (rule 2): replaying one twice is the same as once. So the
 * drain may send a write again after a timeout it could not read, and nothing
 * is made twice.
 *
 * What the person sees: a write shows as done at once, from the cache (the
 * `effect` of `enqueue`); waiting is never shown. Only a write the server
 * refuses for good is, and it keeps its content so it can be made again.
 */

import { all, transact, type Store } from "./idb";
import { needsSignal } from "./online";

export type Method = "POST" | "PUT" | "PATCH" | "DELETE";

/** A write, as an area's helper builds it (`todos.ts`). */
export interface Write {
  method: Method;
  path: string;
  body?: unknown;
  /** The entity it changes: writes to one entity go in order (rule 8). */
  entity: string;
  entityId: string;
  /**
   * The `updated_at` an edit was made from, sent as `X-Based-On` (unit 25C2):
   * the server refuses the edit if the row has changed since. When an earlier
   * write to the same entity lands, this moves to the row it left — the
   * person's own edit is not a change they missed.
   */
  basedOn?: string;
}

export interface Queued extends Write {
  seq: number;
  /** Whose write it is (rule 7). Only that person's session sends it. */
  account: number;
  /** When it was made — the write's day and stamps on the server (rule 3). */
  madeAt: string;
  timeZone: string;
  attempts: number;
  /** Not sent before this time (ms): the backoff after a 5xx or a 429. */
  notBefore: number;
}

/** A write that left the queue without landing (rule 9), kept to be made again. */
export interface Failure {
  seq: number;
  write: Queued;
  status: number | null;
  detail: string;
  at: string;
}

/** Rule 10. Past it the oldest write is dropped — and shown, never lost in silence. */
export const LIMIT = 1000;

/** The change the page listens for: something to show has changed. */
export const CHANNEL = "kindred-offline";

/** Tell every page — and this one — that something to show has changed. */
export function announce(what: "cache" | "failures"): void {
  if (typeof BroadcastChannel === "undefined") return;
  const channel = new BroadcastChannel(CHANNEL);
  channel.postMessage({ type: what });
  channel.close();
}

/** `2026-10-03T23:50:00+01:00`: the moment, with the offset where it was made. */
export function withOffset(when: Date): string {
  const minutes = -when.getTimezoneOffset();
  const sign = minutes >= 0 ? "+" : "-";
  const pad = (n: number) => String(Math.abs(Math.trunc(n))).padStart(2, "0");
  const local = new Date(when.getTime() + minutes * 60_000).toISOString().slice(0, 19);
  return `${local}${sign}${pad(minutes / 60)}:${pad(minutes % 60)}`;
}

export interface EnqueueOptions {
  /** The write's effect on the cache, in the same transaction (rule 9). */
  effect?: (cache: IDBObjectStore) => void;
  now?: Date;
  timeZone?: string;
}

/**
 * Queue `write` for the person the store is bound to (`cache.bindAccount`),
 * and apply its effect to the cache — one transaction, so the screen and the
 * queue never disagree. Refused when no one is bound.
 *
 * **A `PATCH` joins the one before it** (unit 25C2, Q2) when that one is the
 * entity's last queued write, to the same path, and not being sent: the
 * bodies merge, field by field, and the first one's `madeAt` and `basedOn`
 * stay — so the check still sees where the edit began. An afternoon of
 * autosave is then one write, not hundreds against rule 10's limit. A write
 * being sent is left alone: its answer would otherwise take the later fields
 * out of the queue with it.
 */
export function enqueue(
  db: IDBDatabase,
  write: Write,
  options: EnqueueOptions = {},
): Promise<void> {
  const refused = needsSignal(write.method, write.path);
  if (refused !== null) return Promise.reject(new Error(`not queued: ${refused}`));
  const now = options.now ?? new Date();
  const timeZone = options.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const stores: Store[] = ["outbox", "failures", "cache", "meta"];
  let unbound = false;
  return transact<void>(db, stores, "readwrite", (t) => {
    const read = t.objectStore("meta").get("account");
    read.onsuccess = () => {
      const account = read.result as number | undefined;
      if (account === undefined) {
        unbound = true;
        t.abort();
        return;
      }
      const outbox = t.objectStore("outbox");
      const fresh: Omit<Queued, "seq"> = {
        ...write,
        account,
        madeAt: withOffset(now),
        timeZone,
        attempts: 0,
        notBefore: 0,
      };
      options.effect?.(t.objectStore("cache"));
      if (write.method !== "PATCH") {
        outbox.add(fresh);
        bound(t, outbox);
        return;
      }
      const sending = t.objectStore("meta").get("sending");
      sending.onsuccess = () => {
        const queued = outbox.getAll();
        queued.onsuccess = () => {
          const last = (queued.result as Queued[])
            .filter((w) => w.account === account && sameEntity(w, write))
            .at(-1);
          if (
            last &&
            last.method === "PATCH" &&
            last.path === write.path &&
            last.seq !== sending.result
          ) {
            const basedOn = last.basedOn ?? write.basedOn;
            outbox.put({ ...last, body: merged(last.body, write.body), basedOn });
            return;
          }
          outbox.add(fresh);
          bound(t, outbox);
        };
      };
    };
  }).then(
    () => announce("cache"),
    (error: unknown) => {
      throw unbound ? new Error("no person is bound to the offline store") : error;
    },
  );
}

function sameEntity(a: Write, b: Write): boolean {
  return a.entity === b.entity && a.entityId === b.entityId;
}

/** Two partial bodies as one: the later one's fields win. */
function merged(earlier: unknown, later: unknown): unknown {
  const isObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);
  return isObject(earlier) && isObject(later) ? { ...earlier, ...later } : later;
}

/** Rule 10: past the limit, the oldest write goes to the failures, shown. */
function bound(t: IDBTransaction, outbox: IDBObjectStore): void {
  const count = outbox.count();
  count.onsuccess = () => {
    if (count.result <= LIMIT) return;
    const oldest = outbox.openCursor();
    oldest.onsuccess = () => {
      const cursor = oldest.result;
      if (!cursor) return;
      fail(t, cursor.value as Queued, null, "the queue was full: the oldest write was dropped");
      cursor.delete();
    };
  };
}

function fail(t: IDBTransaction, write: Queued, status: number | null, detail: string): void {
  t.objectStore("failures").add({ write, status, detail, at: new Date().toISOString() });
}

export interface DrainDeps {
  fetch: typeof fetch;
  now?: () => number;
}

export interface Drained {
  sent: number;
  failed: number;
  /** Why the drain stopped short, if it did: no network, or no session. */
  held: "offline" | "signed-out" | null;
}

/** 1 s, 2 s, 4 s … at most five minutes. */
export function backoff(attempts: number): number {
  return Math.min(1000 * 2 ** attempts, 5 * 60_000);
}

type Outcome = "done" | "retry" | "permanent" | "signed-out";

/**
 * Rule 9. Any 4xx but 401, 408 and 429 is permanent. A delete that finds
 * nothing is done (rule 2) — the server says 204, and an older one 404.
 */
export function outcome(status: number, method: Method): Outcome {
  if (status < 300 || (status === 404 && method === "DELETE")) return "done";
  if (status === 401) return "signed-out";
  if (status === 408 || status === 429 || status >= 500) return "retry";
  return "permanent";
}

/**
 * The fetch to call, as a plain function. Called as `deps.fetch(…)`, a
 * browser's own `fetch` throws "Illegal invocation" — its `this` must be the
 * window or the worker — and the drain read that as no signal, for ever.
 * Node's `fetch` does not care, so only a browser showed it (unit 26B).
 */
function send(deps: DrainDeps): typeof fetch {
  const { fetch: fetcher } = deps;
  return fetcher;
}

async function sessionAccount(deps: DrainDeps): Promise<number | "offline" | "signed-out"> {
  let response: Response;
  try {
    response = await send(deps)("/api/session", { credentials: "same-origin" });
  } catch {
    return "offline";
  }
  if (!response.ok) return response.status === 401 ? "signed-out" : "offline";
  return ((await response.json()) as { account_id: number }).account_id;
}

function update(db: IDBDatabase, body: (t: IDBTransaction) => void): Promise<void> {
  return transact<void>(db, ["outbox", "failures", "cache", "meta"], "readwrite", body);
}

/** The `updated_at` of the row a write's answer carries, if it carries one. */
async function answeredVersion(response: Response): Promise<string | undefined> {
  try {
    const answer = (await response.json()) as { updated_at?: unknown } | null;
    return typeof answer?.updated_at === "string" ? answer.updated_at : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A write to `landed`'s entity has landed, leaving the row at `version`: the
 * entity's later edits were made after it, so they are based on it — and so
 * is the cached row an edit queued from now on will read its base from.
 */
function advance(t: IDBTransaction, landed: Queued, version: string): void {
  const outbox = t.objectStore("outbox");
  const walk = outbox.openCursor();
  walk.onsuccess = () => {
    const at = walk.result;
    if (!at) return;
    const queued = at.value as Queued;
    if (queued.basedOn !== undefined && sameEntity(queued, landed))
      at.update({ ...queued, basedOn: version });
    at.continue();
  };
  const cache = t.objectStore("cache");
  const read = cache.get([landed.entity, landed.entityId]);
  read.onsuccess = () => {
    const cached = read.result as { row: Record<string, unknown> } | undefined;
    if (cached && "updated_at" in cached.row)
      cache.put({ ...cached, row: { ...cached.row, updated_at: version } });
  };
}

/**
 * Send the queue, oldest first (rule 8).
 *
 * The session is asked first, and only that person's writes go (rule 7):
 * another person signed in on this browser leaves the first person's writes
 * queued, unsent. Writes to one entity go in order — a retry holds the
 * entity's later writes back — and one entity's trouble does not hold
 * another's. No network stops the drain; a 401 holds the queue until sign-in.
 */
async function drainNow(db: IDBDatabase, deps: DrainDeps): Promise<Drained> {
  const now = deps.now ?? Date.now;
  const result: Drained = { sent: 0, failed: 0, held: null };
  const account = await sessionAccount(deps);
  if (typeof account !== "number") return { ...result, held: account };

  const queued = (await all<Queued>(db, "outbox")).filter((write) => write.account === account);
  const held = new Set<string>();
  for (const listed of queued) {
    const key = `${listed.entity}:${listed.entityId}`;
    if (held.has(key)) continue;
    if (listed.notBefore > now()) {
      held.add(key);
      continue;
    }
    // As it is now: an earlier write's answer may have moved its base, and
    // an autosave may have merged into it, since the list was read.
    const write = await claim(db, listed.seq);
    if (write === undefined) continue;
    let response: Response;
    try {
      response = await send(deps)(write.path, {
        method: write.method,
        credentials: "same-origin",
        headers: {
          ...(write.body === undefined ? {} : { "Content-Type": "application/json" }),
          "X-Made-At": write.madeAt,
          "X-Time-Zone": write.timeZone,
          ...(write.basedOn === undefined ? {} : { "X-Based-On": write.basedOn }),
        },
        body: write.body === undefined ? undefined : JSON.stringify(write.body),
      });
    } catch {
      // No backoff: the return of signal wakes the next drain (`online`, or
      // Background Sync, which keeps its own), and a delay would only hold it.
      await update(db, (t) => t.objectStore("meta").delete("sending"));
      await retry(db, write, () => 0);
      return { ...result, held: "offline" };
    }
    const verdict = outcome(response.status, write.method);
    await update(db, (t) => t.objectStore("meta").delete("sending"));
    if (verdict === "signed-out") return { ...result, held: "signed-out" };
    if (verdict === "retry") {
      held.add(key);
      await retry(db, write, (attempts) => now() + backoff(attempts));
      continue;
    }
    const detail = verdict === "permanent" ? await response.text() : "";
    const version = verdict === "done" ? await answeredVersion(response) : undefined;
    await update(db, (t) => {
      t.objectStore("outbox").delete(write.seq);
      if (verdict === "permanent") fail(t, write, response.status, detail);
      if (version !== undefined) advance(t, write, version);
    });
    if (verdict === "permanent") {
      result.failed += 1;
      announce("failures");
    } else {
      result.sent += 1;
    }
  }
  return result;
}

/** The write as it is now, marked as being sent — or nothing, if it has gone. */
function claim(db: IDBDatabase, seq: number): Promise<Queued | undefined> {
  return transact<Queued | undefined>(db, ["outbox", "meta"], "readwrite", (t, result) => {
    const read = t.objectStore("outbox").get(seq);
    read.onsuccess = () => {
      result.value = read.result as Queued | undefined;
      if (result.value) t.objectStore("meta").put(seq, "sending");
    };
  });
}

/** Count the attempt, and set when the write may go again. */
function retry(
  db: IDBDatabase,
  write: Queued,
  notBefore: (attempts: number) => number,
): Promise<void> {
  const attempts = write.attempts + 1;
  return update(db, (t) => {
    t.objectStore("outbox").put({ ...write, attempts, notBefore: notBefore(attempts) });
  });
}

/**
 * `drainNow`, one at a time across the page and the Service Worker: both may
 * be woken by the same return of signal. A second drain would only send
 * writes twice, which is safe — but it would report a refusal twice.
 */
export function drain(db: IDBDatabase, deps: DrainDeps): Promise<Drained> {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (!locks) return drainNow(db, deps);
  // The library types the answer as the callback's promise; it resolves to its value.
  return locks.request("kindred-outbox", () => drainNow(db, deps)).then((drained) => drained);
}

/** What was refused for good, oldest first, to be shown (rule 9). */
export function failures(db: IDBDatabase): Promise<Failure[]> {
  return all<Failure>(db, "failures");
}

/** The person has seen it. */
export function dismiss(db: IDBDatabase, seq: number): Promise<void> {
  return update(db, (t) => t.objectStore("failures").delete(seq)).then(() => announce("failures"));
}
